/** @file Outline as hull: the triangles' single boundary loop, CCW, becomes vertices 0..hull-1. */
import { ringArea } from '@pose2d/autorig/mesh/ringArea';
import { meshOffsets } from '@pose2d/commands/animation/meshOffsets';
import type { MeshAtt } from '@pose2d/format/types';
import type { TopologyPlan, VertexSource } from './blendSources';

/** Directed boundary edges u → w (edges used by exactly one triangle, in that triangle's winding); null on a fork. */
function boundary(tris: readonly number[]): Map<number, number> | null {
  const uses = new Map<number, [number, number, number]>();
  for (let i = 0; i < tris.length; i++) {
    const u = tris[i] ?? 0, w = tris[i % 3 === 2 ? i - 2 : i + 1] ?? 0;
    const key = Math.min(u, w) * 65536 + Math.max(u, w);
    const seen = uses.get(key);
    uses.set(key, [u, w, (seen?.[2] ?? 0) + 1]);
  }
  const next = new Map<number, number>();
  for (const [u, w, n] of uses.values()) {
    if (n !== 1) continue;
    if (next.has(u)) return null;
    next.set(u, w);
  }
  return next;
}

/** The loop through every boundary edge from its smallest vertex, or null when the boundary is not one loop. */
function singleLoop(next: Map<number, number>): number[] | null {
  if (next.size < 3) return null;
  let start = Infinity;
  for (const v of next.keys()) start = Math.min(start, v);
  const loop: number[] = [];
  let v: number | undefined = start;
  while (v !== undefined && loop.length < next.size) {
    loop.push(v);
    v = next.get(v);
  }
  return v === start && loop.length === next.size ? loop : null;
}

/** Plan putting the boundary loop first (counter-clockwise in rest space, y-up), the other vertices after in order. */
export function outlineHullPlan(mesh: MeshAtt): TopologyPlan | null {
  const next = boundary(mesh.triangles);
  const loop = next === null ? null : singleLoop(next);
  if (loop === null) return null;
  const rest = meshOffsets.rest(mesh);
  const ring = loop.flatMap((v) => [rest[2 * v] ?? 0, rest[2 * v + 1] ?? 0]);
  const ccw = ringArea(ring) > 0 ? loop : [loop[0] ?? 0, ...loop.slice(1).reverse()];
  const count = mesh.uvs.length / 2;
  const inLoop = new Set(ccw);
  const order = [...ccw, ...Array.from({ length: count }, (_, v) => v).filter((v) => !inLoop.has(v))];
  const index = new Int32Array(count);
  order.forEach((v, i) => { index[v] = i; });
  const at = (v: number): number => index[v] ?? 0;
  const sources = order.map((v): VertexSource => ({ kind: 'old', v }));
  return { sources, triangles: mesh.triangles.map(at), hull: ccw.length, edges: mesh.edges === undefined ? null : mesh.edges.map(at) };
}
